import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  CheckCircle2,
  MessageSquareText,
  Phone,
  Save,
  Upload,
  UserCheck,
  Users
} from 'lucide-react'
import { parseLineExport, type LineExportMessage } from '../lib/lineExportParser'
import {
  analyzeLineExport,
  lineRiskLabel,
  lineTopicLabel,
  type LineInsightsResult
} from '../lib/lineInsights'
import { hasInternalApi } from '../lib/internalApi'
import { useLineImports, useSaveLineImport } from '../lib/useLineImports'

type LineInsightsPanelProps = {
  liverId: string
  liverName: string
  onRequestAssignmentChange: (handoffNote: string) => void
}

const RISK_TONE = {
  high: 'border-red-100 bg-red-50 text-red-700',
  medium: 'border-amber-100 bg-amber-50 text-amber-700',
  low: 'border-gray-100 bg-gray-50 text-gray-600'
} as const

export function LineInsightsPanel({
  liverId,
  liverName,
  onRequestAssignmentChange
}: LineInsightsPanelProps) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [rawText, setRawText] = useState<string | null>(null)
  const [parsedMessages, setParsedMessages] = useState<LineExportMessage[]>([])
  const [result, setResult] = useState<LineInsightsResult | null>(null)
  const [warnings, setWarnings] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const imports = useLineImports(liverId)
  const saveImport = useSaveLineImport(liverId)
  const apiEnabled = hasInternalApi()

  async function handleFile(file: File | null) {
    if (!file) return
    setError(null)
    setSaveMessage(null)
    saveImport.reset()
    setFileName(file.name)
    try {
      const text = await file.text()
      const parsed = parseLineExport(text)
      setRawText(text)
      setParsedMessages(parsed.messages)
      setWarnings(parsed.warnings.length)
      setResult(analyzeLineExport(parsed.messages, { liverName }))
    } catch (err) {
      setRawText(null)
      setParsedMessages([])
      setResult(null)
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function handleSave() {
    if (!result || !rawText || parsedMessages.length === 0) return
    setSaveMessage(null)
    const response = await saveImport.mutateAsync({
      fileName: fileName ?? 'line-export.txt',
      rawText,
      messages: parsedMessages,
      analysis: result
    })
    setSaveMessage(response.duplicate ? '同じLINE履歴は保存済みです。' : 'LINE履歴を保存しました。')
  }

  return (
    <section className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
            <MessageSquareText className="w-4 h-4 text-emerald-500" />
            LINE履歴分析
          </h2>
          <p className="mt-1 text-xs text-gray-400">
            LINEのトーク履歴txtから担当候補・リスク・引き継ぎメモを生成します。
          </p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".txt,text/plain"
          className="hidden"
          onChange={(event) => void handleFile(event.target.files?.[0] ?? null)}
        />
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Upload className="w-4 h-4" />
            txtを読み込む
          </button>
          <button
            type="button"
            disabled={!apiEnabled || !result || saveImport.isPending}
            onClick={() => void handleSave()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-sm text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
            title={apiEnabled ? '分析結果とLINE履歴を保存します' : 'Internal API設定後に保存できます'}
          >
            <Save className="w-4 h-4" />
            {saveImport.isPending ? '保存中…' : '保存'}
          </button>
        </div>
      </div>

      {fileName && (
        <div className="text-[11px] text-gray-400">
          読み込み: <span className="font-medium text-gray-500">{fileName}</span>
          {warnings > 0 && <span> / 警告 {warnings}件</span>}
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">
          解析に失敗: {error}
        </div>
      )}

      {saveMessage && (
        <div className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
          <CheckCircle2 className="w-3.5 h-3.5" />
          {saveMessage}
        </div>
      )}

      {saveImport.error && (
        <div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">
          保存に失敗: {saveImport.error instanceof Error ? saveImport.error.message : String(saveImport.error)}
        </div>
      )}

      {imports.error && (
        <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
          保存履歴の取得に失敗: {imports.error instanceof Error ? imports.error.message : String(imports.error)}
        </div>
      )}

      {apiEnabled && imports.data && imports.data.length > 0 && (
        <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
          <div className="mb-1 text-[11px] font-semibold text-gray-500">保存済みLINE履歴</div>
          <ul className="space-y-1 text-[11px] text-gray-500">
            {imports.data.slice(0, 3).map((lineImport) => (
              <li key={lineImport.id} className="flex items-center justify-between gap-3">
                <Link
                  to={`/line/imports/${lineImport.id}`}
                  className="min-w-0 truncate text-sky-700 hover:text-sky-800 hover:underline"
                >
                  {lineImport.file_name} / {lineImport.message_count}件
                </Link>
                <span className="shrink-0">
                  {lineImport.date_start ?? '-'}〜{lineImport.date_end ?? '-'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!result ? (
        <div className="rounded-lg border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-400">
          ライバーのLINEトーク履歴txtを読み込むと、ここに分析結果が表示されます。
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            <Metric label="メッセージ" value={`${result.messageCount}件`} />
            <Metric label="接触日" value={`${result.activeDays}日`} />
            <Metric label="最大空白" value={`${result.longestNoContactDays}日`} />
            <Metric label="通話" value={`${result.callEvents.total}回`} />
          </div>

          <div className="grid gap-3 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-gray-600">
                <UserCheck className="w-3.5 h-3.5 text-sky-500" />
                メイン担当候補
              </div>
              {result.primaryManager ? (
                <div>
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-lg font-semibold text-gray-900">
                      {result.primaryManager.name}
                    </span>
                    <span className="text-xs text-gray-400">
                      担当候補内 {(result.primaryManager.share * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {result.primaryManager.styleTags.map((tag) => (
                      <span key={tag} className="rounded bg-white px-2 py-0.5 text-[11px] text-gray-600 border border-gray-100">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-400">担当候補を判定できませんでした。</p>
              )}
            </div>

            <div className="rounded-lg border border-gray-100 px-4 py-3">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-gray-600">
                <Phone className="w-3.5 h-3.5 text-gray-400" />
                担当候補ランキング
              </div>
              <ul className="space-y-2">
                {result.managerCandidates.slice(0, 4).map((manager) => (
                  <li key={manager.name} className="text-xs text-gray-600">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-gray-800">{manager.name}</span>
                      <span>{manager.messageCount}件 / 通話{manager.callStarts}回</span>
                    </div>
                    <div className="mt-1 h-1.5 rounded bg-gray-100">
                      <div
                        className="h-1.5 rounded bg-sky-400"
                        style={{ width: `${Math.max(manager.share * 100, 4)}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {result.handoffSignals.length > 0 && (
            <div className="rounded-lg border border-orange-100 bg-orange-50 px-4 py-3">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-orange-700">
                <Users className="w-3.5 h-3.5" />
                担当交代シグナル
              </div>
              <ul className="space-y-1 text-xs text-orange-700">
                {result.handoffSignals.slice(0, 3).map((signal, index) => (
                  <li key={`${signal.date}-${index}`}>
                    {signal.date}: {signal.actor} → {signal.added}
                    {signal.reason === 'group_add' ? ' / グループ追加' : ' / 月次最多発言者の変化'}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid gap-3 lg:grid-cols-2">
            <div className="rounded-lg border border-gray-100 px-4 py-3">
              <div className="mb-2 text-xs font-semibold text-gray-600">主要トピック</div>
              <ul className="space-y-1.5 text-xs text-gray-600">
                {result.topics.slice(0, 6).map((topic) => (
                  <li key={topic.topic} className="flex items-center justify-between gap-2">
                    <span>{lineTopicLabel(topic.topic)}</span>
                    <span className="text-gray-400">{topic.count}件 / {topic.days}日</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-gray-100 px-4 py-3">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-gray-600">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                注意シグナル
              </div>
              {result.risks.length > 0 ? (
                <ul className="space-y-1.5 text-xs">
                  {result.risks.slice(0, 5).map((risk, index) => (
                    <li
                      key={`${risk.category}-${risk.date}-${index}`}
                      className={`rounded border px-2 py-1.5 ${RISK_TONE[risk.severity]}`}
                    >
                      <span className="font-medium">{lineRiskLabel(risk.category)}</span>
                      <span className="ml-1 text-[11px] opacity-80">{risk.date}</span>
                      <p className="mt-0.5 break-words">{risk.summary}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-gray-400">大きな注意シグナルは見つかっていません。</p>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="text-xs font-semibold text-gray-600">引き継ぎメモ案</div>
              <button
                type="button"
                onClick={() => onRequestAssignmentChange(result.handoffNote)}
                className="rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-gray-800"
              >
                担当変更に使う
              </button>
            </div>
            <pre className="whitespace-pre-wrap font-sans text-xs leading-relaxed text-gray-700">
              {result.handoffNote}
            </pre>
          </div>
        </div>
      )}
    </section>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-gray-100 px-3 py-2">
      <div className="text-[11px] text-gray-400">{label}</div>
      <div className="mt-0.5 text-sm font-semibold text-gray-800">{value}</div>
    </div>
  )
}
